import { useMutation } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useEffect, useMemo, useState } from "react";

import { ApiError, submitAccessRequest } from "../../api/client";
import { isValidEmail } from "../../lib/validation";
import { RequestAccessShell } from "../../components/RequestAccessShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { FormError } from "@/components/ui/form-error";

interface StashedWizardState {
  referralCode: string;
  label: string | null;
}

function readStashedState(): StashedWizardState | null {
  try {
    const raw = sessionStorage.getItem("prm-request-access");
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StashedWizardState;
    if (typeof parsed?.referralCode !== "string") return null;
    return parsed;
  } catch {
    return null;
  }
}

interface FormState {
  first_name: string;
  last_name: string;
  job_title: string;
  company_name: string;
  mobile_phone: string;
  work_email: string;
}

const EMPTY: FormState = {
  first_name: "",
  last_name: "",
  job_title: "",
  company_name: "",
  mobile_phone: "",
  work_email: "",
};

export function RequestAccessDetailsStep() {
  const navigate = useNavigate();
  const stashed = useMemo(readStashedState, []);
  const referralCode = stashed?.referralCode;

  useEffect(() => {
    if (!referralCode) {
      void navigate({ to: "/request-access" });
    }
  }, [navigate, referralCode]);

  const [form, setForm] = useState<FormState>(EMPTY);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [topError, setTopError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () => submitAccessRequest({ ...form, referral_code: referralCode ?? "" }),
    onSuccess: () => {
      const email = form.work_email.toLowerCase();
      sessionStorage.setItem("prm-request-access-email", email);
      sessionStorage.removeItem("prm-request-access");
      void navigate({ to: "/request-access/sent" });
    },
    onError: (err) => {
      setFieldErrors({});
      if (err instanceof ApiError) {
        if (err.status === 429) {
          setTopError("Too many attempts. Wait a minute and try again.");
          return;
        }
        if (err.status === 409) {
          setTopError("We already received a request for that email — we'll be in touch.");
          return;
        }
        // CHP-808: the referral code is verified in step 1 but can go
        // stale/depleted/expired before this submit (access_requests.py returns
        // 400 "referral code is invalid, expired, or depleted"). There's no
        // referral-code input on THIS form, so neither the generic "try again"
        // (never resolves — re-submitting hits the same dead code) nor "fix the
        // highlighted fields" (nothing to highlight) is recoverable. Send the
        // user back to step 1 to enter a fresh code, with an explanation.
        if (err.status === 400) {
          sessionStorage.setItem(
            "prm-request-access-error",
            "That referral code is no longer valid. Please enter a current code.",
          );
          void navigate({ to: "/request-access" });
          return;
        }
        if (err.status === 422 && err.body && typeof err.body === "object") {
          const detail = (err.body as { detail?: unknown }).detail;
          if (Array.isArray(detail)) {
            const fieldMap: Record<string, string> = {};
            for (const d of detail as Array<{ loc?: unknown; msg?: string }>) {
              const loc = Array.isArray(d.loc) ? d.loc : [];
              const field = loc[loc.length - 1];
              if (typeof field === "string" && d.msg) fieldMap[field] = d.msg;
            }
            // A referral_code validation error (min/max length etc.) likewise
            // has no field on this form to highlight — route back to step 1
            // rather than show "fix the highlighted fields" with nothing
            // highlighted (CHP-808).
            if (fieldMap.referral_code) {
              sessionStorage.setItem(
                "prm-request-access-error",
                "That referral code is no longer valid. Please enter a current code.",
              );
              void navigate({ to: "/request-access" });
              return;
            }
            setFieldErrors(fieldMap);
            setTopError("Please fix the highlighted fields.");
            return;
          }
        }
      }
      setTopError("Couldn't submit your request. Try again.");
    },
  });

  // CHP-809: validate required fields + email/phone shape client-side BEFORE
  // firing the rate-limited mutation, mirroring index.tsx's pre-submit guard.
  // An empty/invalid submit no longer round-trips a server 422 to surface what
  // the user could have been told inline immediately.
  function validate(): Record<string, string> {
    const errors: Record<string, string> = {};
    const required: Array<[keyof FormState, string]> = [
      ["first_name", "First name"],
      ["job_title", "Job title"],
      ["company_name", "Company"],
      ["mobile_phone", "Mobile phone"],
      ["work_email", "Work email"],
    ];
    for (const [key, label] of required) {
      if (!form[key].trim()) errors[key] = `${label} is required.`;
    }
    if (form.work_email.trim() && !isValidEmail(form.work_email)) {
      errors.work_email = "Enter a valid email address.";
    }
    // Light phone-shape check: digits (with optional +, spaces, hyphens,
    // parens). The backend is authoritative; this only catches obvious junk.
    if (form.mobile_phone.trim() && !/^[+\d][\d\s()-]{5,}$/.test(form.mobile_phone.trim())) {
      errors.mobile_phone = "Enter a valid phone number.";
    }
    return errors;
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setTopError(null);
    setFieldErrors({});
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setTopError("Please fix the highlighted fields.");
      return;
    }
    submit.mutate();
  }

  function patch<K extends keyof FormState>(key: K, value: string) {
    setForm((s) => ({ ...s, [key]: value }));
  }

  if (!referralCode) return null;

  const fieldRow = (
    id: keyof FormState,
    label: string,
    extra?: { type?: string; placeholder?: string; required?: boolean },
  ) => (
    <div className="space-y-2">
      <Label
        htmlFor={`req-${id}`}
        className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground"
      >
        {label}
      </Label>
      <Input
        id={`req-${id}`}
        type={extra?.type ?? "text"}
        placeholder={extra?.placeholder}
        aria-required={extra?.required ? "true" : undefined}
        aria-invalid={Boolean(fieldErrors[id])}
        value={form[id]}
        onChange={(e) => patch(id, e.target.value)}
      />
      {fieldErrors[id] && (
        <p role="alert" className="text-xs text-destructive">
          {fieldErrors[id]}
        </p>
      )}
    </div>
  );

  return (
    <RequestAccessShell step={2}>
      <p className="text-sm text-muted-foreground">
        Tell us a little about yourself. We'll email you when an admin reviews the request.
      </p>
      <form onSubmit={onSubmit} aria-busy={submit.isPending} noValidate className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {fieldRow("first_name", "First name", { required: true })}
          {fieldRow("last_name", "Last name")}
        </div>
        {fieldRow("job_title", "Job title", { required: true })}
        {fieldRow("company_name", "Company", { required: true })}
        {fieldRow("mobile_phone", "Mobile phone", {
          required: true,
          placeholder: "+61 412 345 678",
        })}
        {fieldRow("work_email", "Work email", {
          required: true,
          type: "email",
        })}

        {topError && <FormError>{topError}</FormError>}
        {/* CHP-809: Back to step 1 (referral code). The stashed code in
            sessionStorage is only cleared on a successful submit, so navigating
            back preserves it — the code step re-reads any stale-code reason and
            re-hydrates its input from the stash (readStashedCode), so the
            entered code is restored for the round trip. */}
        <div className="flex gap-3">
          <Button asChild variant="outline" size="lg">
            <Link to="/request-access">
              <span aria-hidden>←</span> Back
            </Link>
          </Button>
          <Button
            type="submit"
            loading={submit.isPending}
            loadingLabel="Submitting…"
            size="lg"
            className="flex-1"
          >
            Submit request
          </Button>
        </div>
      </form>
    </RequestAccessShell>
  );
}
